import * as React from "react";
import { cn } from "@/lib/utils";
import { CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

export interface DateInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value?: string;
  onChange?: (value: string) => void;
  onEnterPress?: () => void;
  showCalendar?: boolean;
}

const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  ({ className, value = "", onChange, onEnterPress, onKeyDown, showCalendar = true, ...props }, ref) => {
    const [displayValue, setDisplayValue] = React.useState("");
    const [calendarOpen, setCalendarOpen] = React.useState(false);
    const isEditingRef = React.useRef(false);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const pendingCaretRef = React.useRef<number | null>(null);

    // Merge external ref with internal ref
    const setRefs = React.useCallback((node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
    }, [ref]);

    // Helper: derive ISO from current display
    const displayToISO = (display: string): string => {
      const match = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (!match) return "";
      const [, day, month, year] = match;
      const dayNum = parseInt(day);
      const monthNum = parseInt(month);
      const yearNum = parseInt(year);
      if (dayNum >= 1 && dayNum <= 31 && monthNum >= 1 && monthNum <= 12 && yearNum >= 1900) {
        return `${year}-${month}-${day}`;
      }
      return "";
    };

    // Sincroniza valor externo -> display somente quando representa uma data diferente
    // da que já está sendo exibida/digitada (evita apagar edições parciais)
    React.useEffect(() => {
      const currentISO = displayToISO(displayValue);
      if (value) {
        const dateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (dateMatch) {
          if (value !== currentISO) {
            setDisplayValue(`${dateMatch[3]}/${dateMatch[2]}/${dateMatch[1]}`);
          }
        } else if (value !== displayValue) {
          setDisplayValue(value);
        }
      } else if (!isEditingRef.current && displayValue !== "") {
        // Só limpa display se o pai zerou de fato e não estamos no meio de uma edição parcial
        setDisplayValue("");
      }
    }, [value]);

    // Restaura caret depois do commit do React quando há posição pendente
    React.useLayoutEffect(() => {
      if (pendingCaretRef.current !== null && inputRef.current) {
        const pos = pendingCaretRef.current;
        pendingCaretRef.current = null;
        try {
          inputRef.current.setSelectionRange(pos, pos);
        } catch {}
      }
    }, [displayValue]);

    const formatDateInput = (input: string) => {
      const numbers = input.replace(/\D/g, '');
      const limited = numbers.slice(0, 8);
      if (limited.length <= 2) return limited;
      if (limited.length <= 4) return `${limited.slice(0, 2)}/${limited.slice(2)}`;
      return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
    };

    const convertToISODate = (formattedDate: string) => displayToISO(formattedDate);

    // Preserva a posição do cursor após reformatação: mapeia pelo número de
    // dígitos à esquerda do caret no valor cru, pulando os separadores "/".
    const computeCaretPosition = (rawValue: string, rawCaret: number, formatted: string): number => {
      const digitsBefore = rawValue.slice(0, rawCaret).replace(/\D/g, '').length;
      if (digitsBefore <= 0) return 0;
      let seen = 0;
      for (let i = 0; i < formatted.length; i++) {
        if (/\d/.test(formatted[i])) {
          seen++;
          if (seen === digitsBefore) {
            let pos = i + 1;
            if (formatted[pos] === '/') pos++;
            return pos;
          }
        }
      }
      return formatted.length;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      const rawCaret = e.target.selectionStart ?? rawValue.length;
      const formatted = formatDateInput(rawValue);
      pendingCaretRef.current = computeCaretPosition(rawValue, rawCaret, formatted);
      setDisplayValue(formatted);

      if (formatted.length === 10) {
        isEditingRef.current = false;
        const isoDate = convertToISODate(formatted);
        onChange?.(isoDate);
      } else if (formatted.length === 0) {
        // Campo totalmente vazio: propagar limpeza
        isEditingRef.current = false;
        onChange?.("");
      } else {
        // Data parcial: NÃO propagar "" para o pai (evita re-render que apagaria o display)
        isEditingRef.current = true;
      }
    };


    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onEnterPress?.();
      }
      onKeyDown?.(e);
    };

    const handleCalendarSelect = (date: Date | undefined) => {
      if (date) {
        // Formatar a data no fuso horário de Brasília (UTC-3)
        // Usar o dia/mês/ano local da data selecionada para evitar problemas de timezone
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        
        const isoDate = `${year}-${month}-${day}`;
        const formattedDisplay = `${day}/${month}/${year}`;
        
        setDisplayValue(formattedDisplay);
        onChange?.(isoDate);
        setCalendarOpen(false);
      }
    };

    // Converter value ISO para Date para o calendário
    const selectedDate = React.useMemo(() => {
      if (value) {
        const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (match) {
          const [, year, month, day] = match;
          // Criar data usando componentes locais (evita problemas de timezone)
          return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
      }
      return undefined;
    }, [value]);

    return (
      <div className="relative flex items-center">
        <input
          type="text"
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            showCalendar && "pr-9",
            className
          )}
          placeholder="DD/MM/YYYY"
          value={displayValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          ref={setRefs}
          {...props}
        />
        {showCalendar && (
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Abrir calendário"
              >
                <CalendarIcon className="h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleCalendarSelect}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
        )}
      </div>
    );
  }
);

DateInput.displayName = "DateInput";

export { DateInput };
